import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import classNames from 'classnames';
import omit from 'lodash.omit';

import FormGroup from '../FormGroup';
import Button from '../Button';
import getConfig from '../../../util/config';
import { splitToFirstLastName } from '../../../util/text';
import Alert from '../Alert';
import { getErrorString } from '../../../util/normalize-errors';

const config = getConfig();

class UserAddressForm extends Component {
  static propTypes = {
    address: PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      address_line_1: PropTypes.string,
      address_line_2: PropTypes.string,
      city: PropTypes.string,
      state: PropTypes.string,
      post_code: PropTypes.string,
      country: PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    }),
    className: PropTypes.string,
    userId: PropTypes.string,
    createAccountAddress: PropTypes.func,
    updateAccountAddress: PropTypes.func,
    onSuccess: PropTypes.func,
    onAddressCreated: PropTypes.func,
    onAddressUpdated: PropTypes.func,
    onCancel: PropTypes.func,
    formLabel: PropTypes.string,
    handlePlatform: PropTypes.bool,
    submitting: PropTypes.bool,
  };

  static defaultProps = {
    onSuccess: () => {},
    formLabel: 'Shipping Address',
    handlePlatform: true,
  };

  onFormSubmit = async (values, { setSubmitting, setStatus }) => {
    const {
      address,
      createAccountAddress,
      updateAccountAddress,
      onSuccess,
      userId,
      onAddressCreated,
      onAddressUpdated,
      handlePlatform,
    } = this.props;

    setStatus({ error: '' });

    const data = {
      ...omit(values, ['first_name', 'last_name']),
      name: `${values.first_name} ${values.last_name}`,
    };

    if (!handlePlatform) {
      // Will not deal with GQL creation.
      onSuccess({
        ...address,
        ...data,
      });
      setSubmitting(false);
      return;
    }

    try {
      if (address?.id && updateAccountAddress) {
        const response = await updateAccountAddress({
          account_address_id: address.id,
          ...data,
        });
        setSubmitting(false);
        onSuccess(response.data.accountAddressUpdate.account_address);

        if (onAddressUpdated) {
          onAddressUpdated();
        }
      } else {
        if (createAccountAddress) {
          const response = await createAccountAddress(userId, data);
          onSuccess(response.data.accountAddressCreate.account_address);
          setSubmitting(false);

          if (onAddressCreated) {
            onAddressCreated();
          }
          return;
        }

        setSubmitting(false);
        onSuccess({
          ...address,
          ...data,
        });
      }
    } catch (error) {
      setSubmitting(false);
      setStatus({ error: getErrorString(error) });
    }
  };

  getInitialFormState = () => {
    const { address } = this.props;
    const name = address?.name ?? '';
    const { firstName, lastName } = splitToFirstLastName(name);

    return {
      first_name: firstName,
      last_name: lastName,
      address_line_1: address?.address_line_1 ?? '',
      address_line_2: address?.address_line_2 ?? '',
      city: address?.city ?? '',
      state: address?.state ?? '',
      post_code: address?.post_code ?? '',
      country_id: address?.country_id ?? config.country.usa.id,
    };
  };

  render() {
    const { onCancel, className, formLabel, submitting } = this.props;
    const classes = classNames({
      'user-address-form': true,
      [className]: !!className,
    });

    return (
      <Formik
        initialValues={this.getInitialFormState()}
        onSubmit={this.onFormSubmit}
      >
        {({
          values,
          status = {},
          handleSubmit,
          handleChange,
          handleBlur,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit} className={classes}>
            {formLabel && <h4 className="text-center">{formLabel}</h4>}

            <div className="row">
              <div className="col-6">
                <FormGroup
                  id="first-name"
                  name="first_name"
                  type="text"
                  label="First Name"
                  size="sm"
                  value={values.first_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
              </div>

              <div className="col-6">
                <FormGroup
                  id="last-name"
                  name="last_name"
                  type="text"
                  label="Last Name"
                  size="sm"
                  value={values.last_name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
              </div>

              <div className="col-12">
                <FormGroup
                  id="address-line-1"
                  name="address_line_1"
                  type="text"
                  label="Address Line 1"
                  size="sm"
                  value={values.address_line_1}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
              </div>

              <div className="col-12">
                <FormGroup
                  id="address-line-2"
                  name="address_line_2"
                  type="text"
                  label="Address Line 2"
                  size="sm"
                  value={values.address_line_2}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </div>

              <div className="col-6">
                <FormGroup
                  id="city"
                  name="city"
                  type="text"
                  label="City"
                  size="sm"
                  value={values.city}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
              </div>

              <div className="col-6">
                <FormGroup
                  id="state"
                  name="state"
                  type="text"
                  label="State"
                  labelTip="State name or abbreviation."
                  size="sm"
                  value={values.state}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
              </div>

              <div className="col-md-6 col-12">
                <FormGroup
                  id="post-code"
                  name="post_code"
                  type="text"
                  label="Post Code"
                  size="sm"
                  value={values.post_code}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  required
                />
              </div>

              <div className="col-md-6 col-12">
                <FormGroup
                  id="country"
                  type="text"
                  label="Country"
                  name="country"
                  size="sm"
                  defaultValue={config.country.usa.name}
                  readOnly
                />
              </div>

              <div className="col-12">
                {status.error && <Alert type="danger">{status.error}</Alert>}

                <Button
                  type="submit"
                  btnBlock
                  disabled={isSubmitting || submitting}
                  className="mt-2"
                >
                  {isSubmitting || submitting ? 'Saving…' : 'Save'}
                </Button>

                {onCancel && (
                  <Button
                    btnBlock
                    disabled={isSubmitting || submitting}
                    className="mt-1 text-color-base text-center"
                    btnStyle="link"
                    onClick={onCancel}
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          </form>
        )}
      </Formik>
    );
  }
}

export default UserAddressForm;
